import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";

const empty = { name: "", description: "", net_amount: 0, vat_rate: 22, credit_amount: 0, active: true, all_partners: true, assigned_partners: [] };

export default function AdminPackages() {
  const [pkgs, setPkgs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [assignOpen, setAssignOpen] = useState(null);

  const load = useCallback(() => api.get("/recharge-packages").then(r => setPkgs(r.data)), []);
  useEffect(() => { load(); api.get("/partners").then(r => setPartners(r.data)).catch(() => setPartners([])); }, [load]);

  const vat = useMemo(() => Math.round(Number(form.net_amount || 0) * Number(form.vat_rate || 0) / 100 * 100) / 100, [form.net_amount, form.vat_rate]);
  const totalPaid = useMemo(() => Math.round((Number(form.net_amount || 0) + vat) * 100) / 100, [form.net_amount, vat]);
  const bonus = useMemo(() => Math.round((Number(form.credit_amount || 0) - Number(form.net_amount || 0)) * 100) / 100, [form.credit_amount, form.net_amount]);

  const save = async () => {
    if (!form.name || form.name.trim().length < 2) return toast.error("Nome pacchetto obbligatorio");
    try {
      if (editing) await api.put(`/recharge-packages/${editing}`, form);
      else await api.post("/recharge-packages", form);
      toast.success(editing ? "Pacchetto aggiornato" : "Pacchetto creato");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare il pacchetto?")) return;
    await api.delete(`/recharge-packages/${id}`); toast.success("Pacchetto eliminato"); load();
  };
  const edit = (p) => { setEditing(p.id); setForm({ ...empty, ...p }); setOpen(true); };
  const toggleActive = async (p) => {
    await api.put(`/recharge-packages/${p.id}`, { ...p, active: !p.active });
    load();
  };
  const togglePartner = (pid) => {
    setForm((f) => {
      const set = new Set(f.assigned_partners || []);
      if (set.has(pid)) set.delete(pid); else set.add(pid);
      return { ...f, assigned_partners: [...set] };
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-packages">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Pacchetti di Ricarica</h1>
          <p className="text-slate-500 mt-1">Configura i pacchetti di credito prepagato e assegnali ai partner.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="new-package-button">
          <Plus className="w-4 h-4 mr-1" /> Nuovo pacchetto
        </Button>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nome</TableHead><TableHead>Imponibile</TableHead><TableHead>IVA</TableHead>
            <TableHead>Totale</TableHead><TableHead>Credito</TableHead><TableHead>Bonus</TableHead>
            <TableHead>Visibilità</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pkgs.map((p) => (
              <TableRow key={p.id} data-testid={`package-row-${p.id}`}>
                <TableCell className="font-semibold text-slate-900">{p.name}
                  {p.description && <div className="text-xs text-slate-500 font-normal">{p.description}</div>}
                </TableCell>
                <TableCell>{eur(p.net_amount)}</TableCell>
                <TableCell>{p.vat_rate}% · {eur(p.vat_amount)}</TableCell>
                <TableCell className="font-semibold">{eur(p.total)}</TableCell>
                <TableCell className="text-emerald-700 font-semibold">{eur(p.credit_amount)}</TableCell>
                <TableCell className={p.bonus > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}>{p.bonus > 0 ? `+${eur(p.bonus)}` : "—"}</TableCell>
                <TableCell>
                  {p.all_partners ? <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100">Tutti</Badge> : (
                    <Button variant="outline" size="sm" onClick={() => setAssignOpen(p)} data-testid={`view-assignments-${p.id}`}>
                      <Users className="w-3.5 h-3.5 mr-1" /> {(p.assigned_partners || []).length} partner
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <Switch checked={!!p.active} onCheckedChange={() => toggleActive(p)} data-testid={`toggle-package-${p.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => edit(p)} data-testid={`edit-package-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(p.id)} data-testid={`delete-package-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {pkgs.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">Nessun pacchetto configurato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica pacchetto" : "Nuovo pacchetto"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="package-name" /></div>
            <div className="col-span-2"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="package-description" /></div>
            <div><Label>Imponibile €</Label><Input type="number" step="0.01" value={form.net_amount} onChange={(e) => setForm({ ...form, net_amount: parseFloat(e.target.value || "0") })} data-testid="package-net" /></div>
            <div><Label>IVA %</Label><Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: parseInt(e.target.value || "0") })} data-testid="package-vat" /></div>
            <div className="col-span-2"><Label>Credito accreditato €</Label><Input type="number" step="0.01" value={form.credit_amount} onChange={(e) => setForm({ ...form, credit_amount: parseFloat(e.target.value || "0") })} data-testid="package-credit" /></div>
          </div>
          <Card className="border-emerald-200 bg-emerald-50/40 mt-2"><CardContent className="p-3 text-sm grid grid-cols-3 gap-3">
            <div><div className="text-xs uppercase text-slate-500 font-bold">IVA calcolata</div><div className="font-semibold">{eur(vat)}</div></div>
            <div><div className="text-xs uppercase text-slate-500 font-bold">Totale da pagare</div><div className="font-semibold text-[#0F2942]">{eur(totalPaid)}</div></div>
            <div><div className="text-xs uppercase text-emerald-700 font-bold">Bonus</div><div className="font-semibold text-emerald-700">{bonus > 0 ? `+${eur(bonus)}` : "—"}</div></div>
          </CardContent></Card>
          <div className="flex items-center gap-3 mt-2">
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="package-active" />
            <Label>Pacchetto attivo</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!form.all_partners} onCheckedChange={(v) => setForm({ ...form, all_partners: v })} data-testid="package-all-partners" />
            <Label>Disponibile per tutti i partner</Label>
          </div>
          {!form.all_partners && (
            <div className="border rounded-lg p-3 max-h-56 overflow-y-auto">
              <div className="font-semibold text-sm mb-2">Partner assegnati ({(form.assigned_partners || []).length})</div>
              {partners.map((p) => (
                <label key={p.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 px-2 rounded">
                  <Checkbox checked={(form.assigned_partners || []).includes(p.id)} onCheckedChange={() => togglePartner(p.id)} data-testid={`assign-partner-${p.id}`} />
                  <span className="text-sm">{p.company_name} <span className="text-slate-500">· {p.email}</span></span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="save-package">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignOpen} onOpenChange={(v) => { if (!v) setAssignOpen(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Partner assegnati · {assignOpen?.name}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
            {(assignOpen?.assigned_partners || []).length === 0 && <div className="p-4 text-slate-500 text-sm">Nessun partner assegnato.</div>}
            {(assignOpen?.assigned_partners || []).map((pid) => {
              const p = partners.find((x) => x.id === pid);
              return <div key={pid} className="p-3 text-sm"><span className="font-semibold">{p?.company_name || pid}</span> <span className="text-slate-500">· {p?.email || "—"}</span></div>;
            })}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAssignOpen(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
